import { useState } from "react";
import { toast } from "sonner";
import { Plus, X } from "lucide-react";
import { api, apiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import { ProfileMediaGallery } from "../components/MediaGallery";

const GENRES = ["Fantasy", "Thriller", "Romance", "Sci-Fi", "Non-Fiction", "Mystery", "Historical", "Young Adult", "Horror", "Literary Fiction"];

export default function Profile() {
  const { user, setUser } = useAuth();
  const [f, setF] = useState({
    display_name: user?.display_name || "",
    bio: user?.bio || "",
    photo_url: user?.photo_url || "",
    // reader
    favorite_genres: user?.favorite_genres || [],
    goodreads_url: user?.goodreads_url || "",
    storygraph_url: user?.storygraph_url || "",
    amazon_reviewer_url: user?.amazon_reviewer_url || "",
    instagram_url: user?.instagram_url || "",
    tiktok_url: user?.tiktok_url || "",
    youtube_url: user?.youtube_url || "",
    twitter_url: user?.twitter_url || "",
    facebook_url: user?.facebook_url || "",
    threads_url: user?.threads_url || "",
    bluesky_url: user?.bluesky_url || "",
    substack_url: user?.substack_url || "",
    website_url: user?.website_url || "",
    auto_accept_agreement: !!user?.auto_accept_agreement,
    // author
    real_name: user?.real_name || "",
    pen_names: user?.pen_names || [],
    amazon_author_url: user?.amazon_author_url || "",
    auto_accept_enabled: !!user?.auto_accept_enabled,
  });
  const [penInput, setPenInput] = useState("");
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const toggleGenre = (g) => set("favorite_genres", f.favorite_genres.includes(g) ? f.favorite_genres.filter((x) => x !== g) : [...f.favorite_genres, g]);

  const addPen = () => {
    const p = penInput.trim();
    if (!p) return;
    if (f.pen_names.includes(p)) { setPenInput(""); return; }
    set("pen_names", [...f.pen_names, p]);
    setPenInput("");
  };

  const removePen = (p) => set("pen_names", f.pen_names.filter((x) => x !== p));

  const save = async () => {
    setSaving(true);
    try {
      const endpoint = user.role === "author" ? "/profile/author" : "/profile/reader";
      const { data } = await api.put(endpoint, f);
      setUser(data);
      toast.success("Profile saved");
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  if (!user) return null;

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
      <div className="overline mb-2 capitalize">{user.role} profile</div>
      <h1 className="font-serif text-4xl tracking-tight mb-8">Edit profile</h1>

      <div className="paper-card p-6 space-y-6" data-testid="profile-card">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label>Display name{user.role === "author" && " (main pen name)"}</Label>
            <Input value={f.display_name} onChange={(e) => set("display_name", e.target.value)} data-testid="profile-display-name" />
          </div>
          {user.role === "author" && (
            <div>
              <Label>Real name (private)</Label>
              <Input value={f.real_name} onChange={(e) => set("real_name", e.target.value)} data-testid="profile-real-name" />
            </div>
          )}
        </div>

        <div>
          <Label>Bio</Label>
          <Textarea rows={4} value={f.bio} onChange={(e) => set("bio", e.target.value)} data-testid="profile-bio" />
        </div>

        {user.role === "author" && (
          <>
            <div>
              <Label>Additional pen names</Label>
              <div className="flex gap-2 mt-1">
                <Input placeholder="Add a pen name" value={penInput} onChange={(e) => setPenInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addPen(); } }} data-testid="profile-pen-input" />
                <Button type="button" onClick={addPen} variant="outline" data-testid="profile-pen-add"><Plus size={14} /></Button>
              </div>
              {f.pen_names.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-3">
                  {f.pen_names.map((p) => (
                    <Badge key={p} variant="outline" className="pl-3 pr-1 py-1 gap-1">
                      {p}
                      <button onClick={() => removePen(p)} className="hover:text-rose-700" data-testid={`profile-pen-remove-${p}`}><X size={12} /></button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>
            <div>
              <Label>Author photo URL</Label>
              <Input placeholder="https://…" value={f.photo_url} onChange={(e) => set("photo_url", e.target.value)} data-testid="profile-photo" />
            </div>

            <div>
              <div className="overline mb-2">Author profile links</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div><Label>Personal website</Label><Input placeholder="https://…" value={f.website_url} onChange={(e) => set("website_url", e.target.value)} data-testid="profile-website" /></div>
                <div><Label>Goodreads</Label><Input placeholder="https://goodreads.com/…" value={f.goodreads_url} onChange={(e) => set("goodreads_url", e.target.value)} data-testid="profile-author-goodreads" /></div>
                <div><Label>Amazon author page</Label><Input placeholder="https://amazon.com/…" value={f.amazon_author_url} onChange={(e) => set("amazon_author_url", e.target.value)} data-testid="profile-amazon-author" /></div>
                <div><Label>Substack</Label><Input placeholder="https://…substack.com" value={f.substack_url} onChange={(e) => set("substack_url", e.target.value)} data-testid="profile-author-substack" /></div>
              </div>
            </div>

            <div>
              <div className="overline mb-2">Social media</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div><Label>Instagram</Label><Input placeholder="https://instagram.com/…" value={f.instagram_url} onChange={(e) => set("instagram_url", e.target.value)} data-testid="profile-author-instagram" /></div>
                <div><Label>TikTok</Label><Input placeholder="https://tiktok.com/@…" value={f.tiktok_url} onChange={(e) => set("tiktok_url", e.target.value)} data-testid="profile-author-tiktok" /></div>
                <div><Label>YouTube</Label><Input placeholder="https://youtube.com/@…" value={f.youtube_url} onChange={(e) => set("youtube_url", e.target.value)} data-testid="profile-author-youtube" /></div>
                <div><Label>X (Twitter)</Label><Input placeholder="https://x.com/…" value={f.twitter_url} onChange={(e) => set("twitter_url", e.target.value)} data-testid="profile-author-twitter" /></div>
                <div><Label>Facebook</Label><Input placeholder="https://facebook.com/…" value={f.facebook_url} onChange={(e) => set("facebook_url", e.target.value)} data-testid="profile-author-facebook" /></div>
                <div><Label>Threads</Label><Input placeholder="https://threads.net/@…" value={f.threads_url} onChange={(e) => set("threads_url", e.target.value)} data-testid="profile-author-threads" /></div>
                <div><Label>Bluesky</Label><Input placeholder="https://bsky.app/profile/…" value={f.bluesky_url} onChange={(e) => set("bluesky_url", e.target.value)} data-testid="profile-author-bluesky" /></div>
              </div>
            </div>
          </>
        )}

        {user.role === "reader" && (
          <>
            <div>
              <Label>Reader photo URL</Label>
              <Input placeholder="https://…" value={f.photo_url} onChange={(e) => set("photo_url", e.target.value)} data-testid="profile-reader-photo" />
              <p className="text-xs mt-1" style={{ color: "var(--ink-muted)" }}>A friendly headshot authors can see when reviewing your application.</p>
            </div>

            <div>
              <Label>Favorite genres</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {GENRES.map((g) => {
                  const on = f.favorite_genres.includes(g);
                  return (
                    <button
                      key={g}
                      type="button"
                      onClick={() => toggleGenre(g)}
                      className={`btn-press text-xs rounded-full border px-3 py-1.5 ${on ? "" : "opacity-70"}`}
                      style={on ? { borderColor: "#E040FB", background: "rgba(224,64,251,0.15)" } : { borderColor: "var(--border-soft)" }}
                      data-testid={`profile-genre-${g}`}
                    >
                      {g}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <div className="overline mb-2">Reader profile links</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div><Label>Goodreads</Label><Input placeholder="https://goodreads.com/…" value={f.goodreads_url} onChange={(e) => set("goodreads_url", e.target.value)} data-testid="profile-goodreads" /></div>
                <div><Label>The StoryGraph</Label><Input placeholder="https://app.thestorygraph.com/…" value={f.storygraph_url} onChange={(e) => set("storygraph_url", e.target.value)} data-testid="profile-storygraph" /></div>
                <div><Label>Amazon reviewer profile</Label><Input placeholder="https://amazon.com/gp/profile/…" value={f.amazon_reviewer_url} onChange={(e) => set("amazon_reviewer_url", e.target.value)} data-testid="profile-amazon" /></div>
                <div><Label>Substack</Label><Input placeholder="https://…substack.com" value={f.substack_url} onChange={(e) => set("substack_url", e.target.value)} data-testid="profile-substack" /></div>
              </div>
            </div>

            <div>
              <div className="overline mb-2">Social media</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div><Label>Instagram</Label><Input placeholder="https://instagram.com/…" value={f.instagram_url} onChange={(e) => set("instagram_url", e.target.value)} data-testid="profile-instagram" /></div>
                <div><Label>TikTok</Label><Input placeholder="https://tiktok.com/@…" value={f.tiktok_url} onChange={(e) => set("tiktok_url", e.target.value)} data-testid="profile-tiktok" /></div>
                <div><Label>YouTube</Label><Input placeholder="https://youtube.com/@…" value={f.youtube_url} onChange={(e) => set("youtube_url", e.target.value)} data-testid="profile-youtube" /></div>
                <div><Label>X (Twitter)</Label><Input placeholder="https://x.com/…" value={f.twitter_url} onChange={(e) => set("twitter_url", e.target.value)} data-testid="profile-twitter" /></div>
                <div><Label>Facebook</Label><Input placeholder="https://facebook.com/…" value={f.facebook_url} onChange={(e) => set("facebook_url", e.target.value)} data-testid="profile-facebook" /></div>
                <div><Label>Threads</Label><Input placeholder="https://threads.net/@…" value={f.threads_url} onChange={(e) => set("threads_url", e.target.value)} data-testid="profile-threads" /></div>
                <div><Label>Bluesky</Label><Input placeholder="https://bsky.app/profile/…" value={f.bluesky_url} onChange={(e) => set("bluesky_url", e.target.value)} data-testid="profile-bluesky" /></div>
                <div><Label>Personal website</Label><Input placeholder="https://…" value={f.website_url} onChange={(e) => set("website_url", e.target.value)} data-testid="profile-website" /></div>
              </div>
            </div>
          </>
        )}

        <div className="paper-card p-4 flex items-center justify-between gap-4" style={{ background: "#F8FAFC" }}>
          <div>
            <div className="font-serif text-lg">
              {user.role === "author" ? "Enable auto-accept for trusted readers" : "Agree to be auto-accepted by trusted authors"}
            </div>
            <p className="text-sm text-slate-600 max-w-md">
              {user.role === "author"
                ? "Instantly approve applications from readers who opted-in to your auto-accept agreement."
                : "Opt-in to be automatically approved when authors you trust enable auto-accept."}
            </p>
          </div>
          <Switch
            checked={user.role === "author" ? f.auto_accept_enabled : f.auto_accept_agreement}
            onCheckedChange={(v) => set(user.role === "author" ? "auto_accept_enabled" : "auto_accept_agreement", v)}
            data-testid="profile-auto-accept"
          />
        </div>

        <div className="flex justify-end">
          <Button onClick={save} disabled={saving} className="btn-press btn-rainbow" data-testid="profile-save-btn">
            {saving ? "Saving…" : "Save profile"}
          </Button>
        </div>
      </div>

      <div className="paper-card p-6 mt-6">
        <ProfileMediaGallery />
      </div>
    </div>
  );
}
